import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, Zap, RotateCcw, Undo2, Redo2 } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

// A4 dimensions at different DPI
const A4_SIZES = {
  "72": { name: "72 DPI (Web)", width: 595, height: 842, description: "Best for web/email" },
  "96": { name: "96 DPI (Screen)", width: 794, height: 1123, description: "Standard screen resolution" },
  "150": { name: "150 DPI (Draft)", width: 1240, height: 1754, description: "Draft print quality" },
  "300": { name: "300 DPI (Print)", width: 2480, height: 3508, description: "High quality print" },
};

const FIT_MODES = {
  "contain": { name: "Fit Inside", description: "Image fits within A4, may have white borders" },
  "cover": { name: "Fill & Crop", description: "Fills A4 completely, may crop edges" },
  "stretch": { name: "Stretch", description: "Stretches to fill A4 exactly" },
};

// Max history size
const MAX_HISTORY = 20;

function ResizeToA4({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resizeProgress, setResizeProgress] = useState(0);
  const [selectedDPI, setSelectedDPI] = useState("150");
  const [orientation, setOrientation] = useState("portrait");
  const [fitMode, setFitMode] = useState("contain");
  const [outputFormat, setOutputFormat] = useState("original");
  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [pageContent, setPageContent] = useState(null);
  
  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);
  
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Get current A4 dimensions based on orientation
  const getA4Dimensions = () => {
    const base = A4_SIZES[selectedDPI];
    if (orientation === "landscape") {
      return { width: base.height, height: base.width };
    }
    return { width: base.width, height: base.height };
  };

  const a4 = getA4Dimensions();

  // Save to history when images change (but not from undo/redo)
  useEffect(() => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    
    if (images.length > 0 || history.length > 0) {
      // Create a serializable snapshot
      const snapshot = images.map(img => ({
        id: img.id,
        name: img.name,
        file: img.file,
        preview: img.preview,
        originalWidth: img.originalWidth,
        originalHeight: img.originalHeight,
        originalSize: img.originalSize,
        resized: img.resized,
        resizedWidth: img.resizedWidth,
        resizedHeight: img.resizedHeight,
        resizedSize: img.resizedSize,
        resizedPreview: img.resizedPreview,
        outputFormat: img.outputFormat,
        newExtension: img.newExtension,
        mimeType: img.mimeType,
        dpiUsed: img.dpiUsed,
        orientationUsed: img.orientationUsed,
      }));
      
      // Truncate future history if we're not at the end
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(snapshot);
      
      // Limit history size
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }
      
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [images]);

  // Undo function
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setImages(history[prevIndex]);
      toast.info("Undo");
    }
  }, [historyIndex, history]);

  // Redo function
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setImages(history[nextIndex]);
      toast.info("Redo");
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Resize Image to A4 Size - KayTool";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Resize images to A4 paper size for printing or documents. Choose DPI (72-300), orientation, and fit mode. Free online A4 image resizer.');
  }, []);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/resize-to-a4`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
        }
      } catch (error) {
        console.log("No CMS content found");
      }
    };
    fetchPageContent();
  }, [API_URL]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    await processFiles(files);
    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif'));
    
    if (imageFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }

    setIsProcessing(true);
    setResizeProgress(0);

    const newImages = [];

    for (let i = 0; i < imageFiles.length; i++) {
      let file = imageFiles[i];
      
      if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.95 });
          file = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        } catch (error) {
          toast.error(`Failed to convert ${file.name}`);
          continue;
        }
      }

      const dimensions = await getImageDimensions(file);

      newImages.push({
        id: Date.now() + i,
        name: file.name,
        file: file,
        preview: URL.createObjectURL(file),
        originalWidth: dimensions.width,
        originalHeight: dimensions.height,
        originalSize: file.size,
        resized: null,
        resizedWidth: null,
        resizedHeight: null,
        resizedSize: null,
      });

      setResizeProgress(Math.round(((i + 1) / imageFiles.length) * 100));
    }

    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    toast.success(`Added ${newImages.length} image${newImages.length > 1 ? 's' : ''}`);
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const resizeToA4 = async (file, targetWidth, targetHeight) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Fill background
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, targetWidth, targetHeight);

        const sourceRatio = img.width / img.height;
        const targetRatio = targetWidth / targetHeight;

        let drawX = 0, drawY = 0, drawW = targetWidth, drawH = targetHeight;
        let sourceX = 0, sourceY = 0, sourceW = img.width, sourceH = img.height;

        if (fitMode === "contain") {
          if (sourceRatio > targetRatio) {
            drawW = targetWidth;
            drawH = targetWidth / sourceRatio;
            drawY = (targetHeight - drawH) / 2;
          } else {
            drawH = targetHeight;
            drawW = targetHeight * sourceRatio;
            drawX = (targetWidth - drawW) / 2;
          }
          ctx.drawImage(img, drawX, drawY, drawW, drawH);
        } else if (fitMode === "cover") {
          if (sourceRatio > targetRatio) {
            sourceW = img.height * targetRatio;
            sourceX = (img.width - sourceW) / 2;
          } else {
            sourceH = img.width / targetRatio;
            sourceY = (img.height - sourceH) / 2;
          }
          ctx.drawImage(img, sourceX, sourceY, sourceW, sourceH, 0, 0, targetWidth, targetHeight);
        } else {
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
        }
        
        let mimeType = file.type || 'image/jpeg';
        let quality = 0.92;
        
        if (outputFormat !== 'original') {
          if (outputFormat === 'png') {
            mimeType = 'image/png';
            quality = 1.0;
          } else if (outputFormat === 'jpg' || outputFormat === 'jpeg') {
            mimeType = 'image/jpeg';
          }
        }
        
        canvas.toBlob((blob) => {
          resolve({ blob, mimeType });
        }, mimeType, quality);
        
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleResize = async () => {
    if (images.length === 0) {
      toast.error("Please add images first");
      return;
    }

    setIsProcessing(true);
    setResizeProgress(0);

    const updatedImages = [];
    const dims = getA4Dimensions();

    for (let i = 0; i < images.length; i++) {
      const image = images[i];

      try {
        const { blob: resizedBlob, mimeType } = await resizeToA4(image.file, dims.width, dims.height);
        
        let newExtension = '';
        if (outputFormat === 'png') newExtension = '.png';
        else if (outputFormat === 'jpg') newExtension = '.jpg';
        else if (outputFormat === 'jpeg') newExtension = '.jpeg';
        
        updatedImages.push({
          ...image,
          resized: resizedBlob,
          resizedWidth: dims.width,
          resizedHeight: dims.height,
          resizedSize: resizedBlob.size,
          resizedPreview: URL.createObjectURL(resizedBlob),
          outputFormat: outputFormat,
          newExtension: newExtension,
          mimeType: mimeType,
          dpiUsed: selectedDPI,
          orientationUsed: orientation,
        });
      } catch (error) {
        toast.error(`Failed to resize ${image.name}`);
        updatedImages.push(image);
      }

      setResizeProgress(Math.round(((i + 1) / images.length) * 100));
    }

    setImages(updatedImages);
    setIsProcessing(false);
    toast.success(`Resized to A4 (${dims.width}×${dims.height}px)!`);
  };

  const downloadSingle = (image) => {
    if (!image.resized) return;
    let ext = image.newExtension ? image.newExtension.replace('.', '') : (image.file.type === 'image/png' ? 'png' : 'jpg');
    const newName = image.name.replace(/\.[^/.]+$/, `_A4_${image.dpiUsed}dpi.${ext}`);
    saveAs(image.resized, newName);
  };

  const downloadAll = async () => {
    const resizedImages = images.filter(img => img.resized);
    if (resizedImages.length === 0) {
      toast.error("No resized images to download");
      return;
    }

    if (resizedImages.length === 1) {
      downloadSingle(resizedImages[0]);
      return;
    }

    const zip = new JSZip();
    resizedImages.forEach((image) => {
      let ext = image.newExtension ? image.newExtension.replace('.', '') : (image.file.type === 'image/png' ? 'png' : 'jpg');
      const newName = image.name.replace(/\.[^/.]+$/, `_A4_${image.dpiUsed}dpi.${ext}`);
      zip.file(newName, image.resized);
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, `A4_images_${selectedDPI}dpi.zip`);
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const clearAll = () => {
    setImages([]);
    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Reset history
    setHistory([]);
    setHistoryIndex(-1);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-amber-50 to-orange-50'}`}>
      <Toaster position="top-center" richColors />
      
      {!embedded && (
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
            </a>
            <div className="flex items-center gap-4">
              <ToolsNav />
              <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
                <Info className="w-3 h-3" /> 100% Local Processing
              </Badge>
              <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </header>
      )}

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-full mb-4">
            <FileText className="w-5 h-5" />
            <span className="font-medium">A4 Paper Size</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Resize Image to A4 Size
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Perfect for printing documents, presentations, and professional materials. Choose your DPI and orientation.
          </p>
        </div>

        {/* Settings Card */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6 space-y-6">
            {/* DPI Selection */}
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                Resolution (DPI)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(A4_SIZES).map(([dpi, info]) => (
                  <button
                    key={dpi}
                    onClick={() => setSelectedDPI(dpi)}
                    data-testid={`dpi-${dpi}`}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      selectedDPI === dpi 
                        ? 'border-transparent bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg scale-105' 
                        : 'border-slate-200 dark:border-slate-700 hover:border-amber-300 dark:hover:border-amber-600 bg-white dark:bg-slate-800'
                    }`}
                  >
                    <p className={`text-lg font-bold ${selectedDPI === dpi ? 'text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                      {dpi} DPI
                    </p>
                    <p className={`text-xs mt-1 ${selectedDPI === dpi ? 'text-white/80' : 'text-slate-500'}`}>
                      {info.width} × {info.height}
                    </p>
                    <p className={`text-xs mt-1 ${selectedDPI === dpi ? 'text-white/70' : 'text-slate-400'}`}>
                      {info.description}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Orientation */}
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                Orientation
              </Label>
              <div className="flex gap-3">
                <Button
                  variant={orientation === "portrait" ? "default" : "outline"}
                  onClick={() => setOrientation("portrait")}
                  className={`flex-1 gap-2 ${orientation === "portrait" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                  data-testid="orientation-portrait"
                >
                  <div className="w-4 h-6 border-2 border-current rounded-sm" />
                  Portrait
                </Button>
                <Button
                  variant={orientation === "landscape" ? "default" : "outline"}
                  onClick={() => setOrientation("landscape")}
                  className={`flex-1 gap-2 ${orientation === "landscape" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                  data-testid="orientation-landscape"
                >
                  <div className="w-6 h-4 border-2 border-current rounded-sm" />
                  Landscape
                </Button>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 text-center">
                Output: {a4.width} × {a4.height} pixels
              </p>
            </div>

            {/* Fit Mode */}
            <div>
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                Fit Mode
              </Label>
              <RadioGroup value={fitMode} onValueChange={setFitMode} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {Object.entries(FIT_MODES).map(([mode, info]) => (
                  <div key={mode} className="flex items-center space-x-2">
                    <RadioGroupItem value={mode} id={`fit-${mode}`} />
                    <Label htmlFor={`fit-${mode}`} className="cursor-pointer flex-1">
                      <span className="font-medium">{info.name}</span>
                      <p className="text-xs text-slate-500">{info.description}</p>
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Background Color (for contain mode) */}
            {fitMode === "contain" && (
              <div>
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                  Background Color
                </Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-10 h-10 rounded cursor-pointer border border-slate-300"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{backgroundColor}</span>
                  <Button variant="outline" size="sm" onClick={() => setBackgroundColor("#ffffff")}>
                    <RotateCcw className="w-4 h-4 mr-1" /> White
                  </Button>
                </div>
              </div>
            )}

            {/* Output Format */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 block">
                Output Format
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "original", label: "Original" },
                  { value: "png", label: "PNG" },
                  { value: "jpg", label: "JPG" },
                  { value: "jpeg", label: "JPEG" },
                ].map((format) => (
                  <Button
                    key={format.value}
                    variant={outputFormat === format.value ? "default" : "outline"}
                    size="sm"
                    onClick={() => setOutputFormat(format.value)}
                    className={outputFormat === format.value ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                  >
                    {format.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
            isDragging ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div 
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-amber-600" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                Drop, paste, or click to upload
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Supports JPEG, PNG, WebP, GIF, HEIC
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm">
                <FileText className="w-4 h-4" />
                A4 {orientation === "portrait" ? "Portrait" : "Landscape"}: {a4.width} × {a4.height}px @ {selectedDPI} DPI
              </div>
            </div>
          </CardContent>
        </Card>

        {isProcessing && (
          <Card className="mt-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Progress value={resizeProgress} className="h-3" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {resizeProgress}%
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {images.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Images ({images.length})
              </h3>
              <div className="flex gap-2 flex-wrap">
                {/* Undo/Redo Buttons */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={undo}
                  disabled={!canUndo}
                  title="Undo (Ctrl+Z)"
                  data-testid="undo-button"
                >
                  <Undo2 className="w-4 h-4 mr-1" /> Undo
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={redo}
                  disabled={!canRedo}
                  title="Redo (Ctrl+Y or Ctrl+Shift+Z)"
                  data-testid="redo-button"
                >
                  <Redo2 className="w-4 h-4 mr-1" /> Redo
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                <Button 
                  onClick={handleResize} 
                  disabled={isProcessing}
                  data-testid="resize-button"
                  className="bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Resize to A4
                </Button>
              </div>
            </div>

            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="dark:bg-slate-800 overflow-hidden" data-testid={`image-card-${image.id}`}>
                  <div className="aspect-[3/4] relative bg-slate-100 dark:bg-slate-700">
                    <img
                      src={image.resizedPreview || image.preview}
                      alt={image.name}
                      className="w-full h-full object-contain"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 w-8 h-8"
                      onClick={() => removeImage(image.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    {image.resized && (
                      <Badge className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-orange-600">
                        A4 {image.dpiUsed} DPI
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate mb-2">{image.name}</p>
                    <div className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                      <p>Original: {image.originalWidth} × {image.originalHeight}</p>
                      {image.resized && (
                        <p className="text-green-600 dark:text-green-400">
                          A4: {image.resizedWidth} × {image.resizedHeight} ({formatSize(image.resizedSize)})
                        </p>
                      )}
                    </div>
                    {image.resized && (
                      <Button
                        size="sm"
                        className="w-full mt-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90"
                        onClick={() => downloadSingle(image)}
                        data-testid={`download-${image.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" /> Download
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {images.some(img => img.resized) && images.length > 1 && (
              <div className="mt-4 text-center">
                <Button onClick={downloadAll} size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:opacity-90" data-testid="download-all">
                  <Download className="w-5 h-5 mr-2" /> Download All (ZIP)
                </Button>
              </div>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>

        {/* Info Section */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: FileText, title: "Print Ready", desc: "Perfect A4 dimensions for professional printing" },
            { icon: Zap, title: "Multiple DPI", desc: "72-300 DPI options for web or print quality" },
            { icon: Info, title: "100% Private", desc: "All processing happens in your browser" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <feature.icon className="w-10 h-10 mx-auto mb-3 text-amber-600" />
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* A4 Size Reference */}
        <Card className="mt-8 dark:bg-slate-800">
          <CardContent className="p-6">
            <h4 className="font-semibold text-slate-800 dark:text-white mb-4">A4 Size Reference</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Physical Size</p>
                <p className="font-medium text-slate-700 dark:text-slate-300">210 × 297 mm</p>
              </div>
              <div>
                <p className="text-slate-500">72 DPI (Web)</p>
                <p className="font-medium text-slate-700 dark:text-slate-300">595 × 842 px</p>
              </div>
              <div>
                <p className="text-slate-500">150 DPI (Draft)</p>
                <p className="font-medium text-slate-700 dark:text-slate-300">1240 × 1754 px</p>
              </div>
              <div>
                <p className="text-slate-500">300 DPI (Print)</p>
                <p className="font-medium text-slate-700 dark:text-slate-300">2480 × 3508 px</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {pageContent?.content && (
        <div className="bg-slate-50 dark:bg-slate-800/50 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}

      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ResizeToA4;
